/**
 * InsightPro™ — Landing Page v3.0
 * Full SaaS product positioning. No client data.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL;

const css = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Instrument+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=JetBrains+Mono:wght@300;400;500&display=swap');

.lp *,.lp *::before,.lp *::after{box-sizing:border-box;margin:0;padding:0}
.lp{
  --void:#05050a;--void2:#090910;--void3:#0d0d18;
  --gold:#c4922a;--gold-l:#dba93a;--gold-ll:#f0c85a;
  --gold-dim:rgba(196,146,42,0.08);--gold-b:rgba(196,146,42,0.20);
  --t1:#eeeef8;--t2:#9898b8;--t3:#50506a;
  --green:#17a85c;--red:#c44040;--amber:#c47c1a;
  --border:rgba(255,255,255,0.055);--border2:rgba(255,255,255,0.10);
  --fd:'Syne',sans-serif;--fb:'Instrument Sans',sans-serif;--fm:'JetBrains Mono',monospace;
  font-family:var(--fb);background:var(--void);color:var(--t1);
  overflow-x:hidden;width:100%;min-width:0;
}

/* NAV */
.lp-nav{
  position:fixed;top:0;left:0;right:0;z-index:100;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 52px;height:64px;
  background:rgba(5,5,10,0.92);backdrop-filter:blur(24px);
  border-bottom:1px solid var(--border);
}
.lp-nav-logo{display:flex;align-items:center;gap:12px;cursor:pointer;text-decoration:none}
.lp-logo-hex{
  width:34px;height:34px;flex-shrink:0;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);
  display:flex;align-items:center;justify-content:center;
}
.lp-logo-hex span{font-family:var(--fd);font-weight:700;font-size:11px;color:#05050a}
.lp-brand{font-family:var(--fd);font-size:17px;font-weight:700;color:var(--t1);letter-spacing:-0.02em}
.lp-brand sup{font-size:11px;color:var(--gold);vertical-align:super}
.lp-nav-links{display:flex;align-items:center;gap:4px}
.lp-nav-links a{
  font-size:14px;font-weight:400;color:var(--t3);text-decoration:none;
  padding:6px 14px;border-radius:4px;cursor:pointer;transition:color .18s,background .18s;
}
.lp-nav-links a:hover{color:var(--t1);background:rgba(255,255,255,0.04)}
.lp-nav-right{display:flex;align-items:center;gap:10px}
.lp-btn-ghost{
  font-size:14px;font-weight:400;color:var(--t2);background:transparent;
  border:1px solid var(--border2);border-radius:6px;padding:7px 18px;
  cursor:pointer;transition:all .18s;font-family:var(--fb);
}
.lp-btn-ghost:hover{color:var(--t1);border-color:rgba(255,255,255,0.2)}
.lp-btn-gold{
  font-size:14px;font-weight:600;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  color:#05050a;border:none;border-radius:6px;padding:8px 20px;
  cursor:pointer;transition:opacity .18s;font-family:var(--fb);letter-spacing:.01em;
}
.lp-btn-gold:hover{opacity:.88}
.lp-hamburger{display:none;background:none;border:none;color:var(--t1);font-size:20px;cursor:pointer}

/* HERO */
.lp-hero{
  padding:140px 52px 80px;
  background:radial-gradient(ellipse 80% 60% at 50% -10%,rgba(196,146,42,0.07),transparent);
  border-bottom:1px solid var(--border);
  position:relative;overflow:hidden;
}
.lp-hero::before{
  content:'';position:absolute;inset:0;
  background:repeating-linear-gradient(0deg,transparent,transparent 80px,rgba(255,255,255,0.012) 80px,rgba(255,255,255,0.012) 81px),
             repeating-linear-gradient(90deg,transparent,transparent 80px,rgba(255,255,255,0.012) 80px,rgba(255,255,255,0.012) 81px);
  pointer-events:none;
}
.lp-hero-inner{
  max-width:1200px;margin:0 auto;
  display:grid;grid-template-columns:1fr 1.1fr;gap:72px;align-items:center;
  position:relative;z-index:1;
}
.lp-eyebrow{
  display:inline-flex;align-items:center;gap:8px;
  font-family:var(--fm);font-size:11px;letter-spacing:.18em;text-transform:uppercase;
  color:var(--gold);margin-bottom:20px;
  background:var(--gold-dim);border:1px solid var(--gold-b);
  padding:5px 12px;border-radius:20px;
}
.lp-eyebrow::before{content:'';width:5px;height:5px;border-radius:50%;background:var(--gold);flex-shrink:0}
.lp-h1{
  font-family:var(--fd);font-size:clamp(28px,4.5vw,60px);
  font-weight:800;line-height:1.08;letter-spacing:-.02em;color:var(--t1);
  margin-bottom:24px;word-break:break-word;overflow-wrap:break-word;
}
.lp-h1 em{font-style:normal;color:var(--gold-l)}
.lp-hero-sub{
  font-size:17px;color:var(--t2);line-height:1.75;font-weight:300;
  max-width:480px;margin-bottom:36px;
}
.lp-hero-actions{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:36px}
.lp-btn-primary{
  font-size:15px;font-weight:600;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  color:#05050a;border:none;border-radius:8px;padding:14px 28px;
  cursor:pointer;transition:opacity .18s,transform .18s;font-family:var(--fb);
  letter-spacing:.01em;
}
.lp-btn-primary:hover{opacity:.9;transform:translateY(-1px)}
.lp-btn-outline{
  font-size:15px;font-weight:400;color:var(--t1);
  background:transparent;border:1px solid var(--border2);
  border-radius:8px;padding:13px 24px;cursor:pointer;
  transition:all .18s;font-family:var(--fb);
}
.lp-btn-outline:hover{border-color:var(--gold-b);color:var(--gold-l)}
.lp-hero-proof{display:flex;gap:20px;flex-wrap:wrap}
.lp-proof-item{display:flex;flex-direction:column;gap:2px}
.lp-proof-val{font-family:var(--fd);font-size:24px;font-weight:800;color:var(--t1);letter-spacing:-.02em}
.lp-proof-val span{font-size:14px;color:var(--gold)}
.lp-proof-lbl{font-family:var(--fm);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--t3)}

/* PHONE MOCKUP */
.lp-phone-wrap{
  position:relative;display:flex;justify-content:center;align-items:center;
  width:100%;
}
.lp-phone-glow{
  position:absolute;width:320px;height:320px;border-radius:50%;
  background:radial-gradient(circle,rgba(196,146,42,0.15),transparent 70%);
  top:50%;left:50%;transform:translate(-50%,-50%);pointer-events:none;
}
.lp-phone{
  width:260px;background:#0d0d14;border-radius:36px;
  border:2px solid rgba(255,255,255,0.12);
  box-shadow:0 40px 80px rgba(0,0,0,0.6),0 0 0 1px rgba(255,255,255,0.04),inset 0 1px 0 rgba(255,255,255,0.06);
  overflow:hidden;position:relative;z-index:2;
}
.lp-phone-notch{
  height:28px;background:#05050a;display:flex;align-items:center;justify-content:center;
}
.lp-phone-notch-bar{width:80px;height:14px;background:#111;border-radius:7px}
.lp-phone-screen{padding:0;background:#f0f2f5}
.lp-phone-topbar{
  background:#0a0a10;padding:8px 12px;
  display:flex;align-items:center;justify-content:space-between;
}
.lp-phone-topbar-logo{display:flex;align-items:center;gap:6px}
.lp-phone-topbar-hex{
  width:20px;height:20px;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);
  display:flex;align-items:center;justify-content:center;
}
.lp-phone-topbar-hex span{font-size:6px;font-weight:800;color:#0a0a10;font-family:var(--fd)}
.lp-phone-topbar-brand{font-size:10px;font-weight:700;color:#eeeef8;letter-spacing:-.01em;font-family:var(--fd)}
.lp-phone-topbar-brand sup{font-size:6px;color:var(--gold)}
.lp-phone-period{
  display:flex;gap:3px;padding:6px 8px;background:#0a0a10;
}
.lp-pp{font-size:8px;color:#50506a;padding:3px 6px;border-radius:3px;font-family:'JetBrains Mono',monospace;letter-spacing:.05em}
.lp-pp.act{background:var(--gold);color:#05050a;font-weight:700}
.lp-phone-kpis{display:grid;grid-template-columns:1fr 1fr;gap:6px;padding:8px}
.lp-phone-kpi{
  background:#fff;border-radius:8px;padding:8px 10px;
  box-shadow:0 1px 4px rgba(0,0,0,0.06);
}
.lp-pk-lbl{font-size:7px;letter-spacing:.1em;text-transform:uppercase;color:#888;font-family:'JetBrains Mono',monospace;margin-bottom:3px}
.lp-pk-val{font-size:13px;font-weight:800;color:#0a0a10;font-family:'Syne',sans-serif;letter-spacing:-.02em}
.lp-pk-sub{font-size:7px;color:#17a85c;margin-top:2px;font-family:'JetBrains Mono',monospace}
.lp-pk-sub.dn{color:#c44040}
.lp-phone-section-lbl{
  padding:4px 8px 2px;font-size:7px;letter-spacing:.12em;text-transform:uppercase;
  color:#888;font-family:'JetBrains Mono',monospace;
}
.lp-phone-branch-rows{padding:0 8px 8px;display:flex;flex-direction:column;gap:5px}
.lp-pbr{
  background:#fff;border-radius:6px;padding:6px 8px;
  display:flex;align-items:center;gap:6px;
  box-shadow:0 1px 3px rgba(0,0,0,0.05);
}
.lp-pbr-num{font-size:7px;color:#aaa;width:14px;font-family:'JetBrains Mono',monospace}
.lp-pbr-name{font-size:8px;font-weight:600;color:#0a0a10;flex:1;font-family:'Instrument Sans',sans-serif}
.lp-pbr-val{font-size:8px;font-weight:700;color:#0a0a10;font-family:'Syne',sans-serif}
.lp-pbr-grow{font-size:7px;font-family:'JetBrains Mono',monospace;width:32px;text-align:right}
.lp-pbr-bar{height:2px;background:var(--gold);border-radius:1px;margin-top:3px}

/* SECOND PHONE (features) */
.lp-phone2{
  position:absolute;right:-40px;top:60px;
  width:200px;background:#0d0d14;border-radius:28px;
  border:2px solid rgba(255,255,255,0.10);
  box-shadow:0 20px 50px rgba(0,0,0,0.5);
  overflow:hidden;z-index:1;
  opacity:0.85;
}
.lp-phone2-screen{padding:8px;background:#f0f2f5}
.lp-phone2-header{
  background:#0a0a10;padding:6px 8px;
  display:flex;align-items:center;justify-content:space-between;
}
.lp-phone2-title{font-size:8px;color:var(--gold);font-family:'JetBrains Mono',monospace;letter-spacing:.1em;text-transform:uppercase}
.lp-phone2-live{font-size:7px;color:#17a85c;font-family:'JetBrains Mono',monospace;letter-spacing:.08em}
.lp-phone2-rows{padding:4px;display:flex;flex-direction:column;gap:3px}
.lp-p2r{
  background:#fff;border-radius:5px;padding:5px 7px;
  display:flex;align-items:center;justify-content:space-between;
}
.lp-p2r-name{font-size:7px;font-weight:600;color:#0a0a10;font-family:'Instrument Sans',sans-serif}
.lp-p2r-val{font-size:8px;font-weight:700;color:#0a0a10;font-family:'Syne',sans-serif}
.lp-p2r-delta{font-size:7px;font-family:'JetBrains Mono',monospace}

/* RIBBON */
.lp-ribbon{
  padding:0;background:rgba(196,146,42,0.04);
  border-top:1px solid var(--gold-b);border-bottom:1px solid var(--gold-b);
  overflow:hidden;height:44px;display:flex;align-items:center;
}
.lp-ribbon-track{
  display:flex;gap:0;white-space:nowrap;
  animation:ribbonScroll 28s linear infinite;
}
@keyframes ribbonScroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.lp-ribbon-item{
  font-family:var(--fm);font-size:12px;letter-spacing:.12em;text-transform:uppercase;
  color:var(--t3);padding:0 28px;display:flex;align-items:center;gap:10px;
}
.lp-ribbon-dot{width:3px;height:3px;border-radius:50%;background:var(--gold);flex-shrink:0}

/* HOW IT WORKS */
.lp-hiw{
  padding:100px 52px;background:var(--void2);
  border-bottom:1px solid var(--border);
}
.lp-hiw-inner{max-width:1200px;margin:0 auto}
.lp-hiw-header{text-align:center;margin-bottom:72px}
.lp-section-eyebrow{
  font-family:var(--fm);font-size:11px;letter-spacing:.18em;text-transform:uppercase;
  color:var(--gold);margin-bottom:16px;display:flex;align-items:center;justify-content:center;gap:10px;
}
.lp-section-eyebrow::before,.lp-section-eyebrow::after{content:'';flex:0 0 32px;height:1px;background:var(--gold-b)}
.lp-section-title{
  font-family:var(--fd);font-size:clamp(28px,3.5vw,44px);
  font-weight:800;line-height:1.1;letter-spacing:-.03em;color:var(--t1);margin-bottom:16px;
}
.lp-section-title em{font-style:italic;color:var(--gold-l)}
.lp-section-sub{font-size:16px;color:var(--t2);line-height:1.7;font-weight:300;max-width:520px;margin:0 auto}
.lp-hiw-steps{
  display:grid;grid-template-columns:repeat(3,1fr);gap:0;
  position:relative;
}
.lp-hiw-steps::before{
  content:'';position:absolute;top:52px;left:calc(16.67% + 28px);right:calc(16.67% + 28px);
  height:1px;background:linear-gradient(90deg,var(--gold-b),var(--gold-b));
  border-top:1px dashed var(--gold-b);
}
.lp-step{
  display:flex;flex-direction:column;align-items:center;text-align:center;
  padding:0 32px;position:relative;
}
.lp-step-num{
  width:56px;height:56px;border-radius:50%;
  border:1px solid var(--gold-b);background:var(--gold-dim);
  display:flex;align-items:center;justify-content:center;
  font-family:var(--fd);font-size:20px;font-weight:800;color:var(--gold-l);
  margin-bottom:28px;position:relative;z-index:1;
  box-shadow:0 0 0 8px var(--void2);
}
.lp-step-title{font-family:var(--fd);font-size:18px;font-weight:700;color:var(--t1);margin-bottom:12px;letter-spacing:-.01em}
.lp-step-text{font-size:15px;color:var(--t2);line-height:1.7;font-weight:300}
.lp-step-tech{
  margin-top:16px;display:flex;flex-wrap:wrap;justify-content:center;gap:6px;
}
.lp-tech-pill{
  font-family:var(--fm);font-size:10px;letter-spacing:.1em;
  color:var(--t3);background:rgba(255,255,255,0.04);
  border:1px solid var(--border);border-radius:3px;padding:3px 8px;
}

/* MODULES */
.lp-modules{
  padding:100px 52px;background:var(--void);
  border-bottom:1px solid var(--border);
}
.lp-modules-inner{max-width:1200px;margin:0 auto}
.lp-modules-header{margin-bottom:64px}
.lp-modules-grid{
  display:grid;grid-template-columns:repeat(3,1fr);gap:1px;
  background:var(--border);border:1px solid var(--border);border-radius:8px;overflow:hidden;
}
.lp-module{
  background:var(--void);padding:36px 32px;
  transition:background .2s;cursor:default;position:relative;overflow:hidden;
}
.lp-module::before{
  content:'';position:absolute;top:0;left:0;right:0;height:2px;
  background:linear-gradient(90deg,transparent,var(--gold-b),transparent);
  opacity:0;transition:opacity .2s;
}
.lp-module:hover{background:rgba(196,146,42,0.03)}
.lp-module:hover::before{opacity:1}
.lp-module-icon{
  width:40px;height:40px;border:1px solid var(--gold-b);border-radius:6px;
  display:flex;align-items:center;justify-content:center;
  font-size:18px;margin-bottom:20px;background:var(--gold-dim);
}
.lp-module-name{font-family:var(--fd);font-size:16px;font-weight:700;color:var(--t1);margin-bottom:8px;letter-spacing:-.01em}
.lp-module-text{font-size:14px;color:var(--t2);line-height:1.65;font-weight:300;margin-bottom:16px}
.lp-module-features{display:flex;flex-direction:column;gap:6px}
.lp-mf{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--t3)}
.lp-mf::before{content:'';width:4px;height:4px;border-radius:50%;background:var(--gold);flex-shrink:0}

/* PRODUCT SCREENS */
.lp-screens{
  padding:100px 52px;background:var(--void2);
  border-bottom:1px solid var(--border);
}
.lp-screens-inner{max-width:1200px;margin:0 auto}
.lp-screens-header{text-align:center;margin-bottom:64px}
.lp-screens-grid{
  display:grid;grid-template-columns:1fr 1fr;gap:24px;
}
.lp-screen-card{
  border:1px solid var(--border);border-radius:12px;overflow:hidden;
  background:var(--void3);transition:border-color .2s,transform .2s;
}
.lp-screen-card:hover{border-color:var(--gold-b);transform:translateY(-2px)}
.lp-screen-label{
  padding:12px 16px;background:rgba(255,255,255,0.02);
  border-bottom:1px solid var(--border);
  display:flex;align-items:center;justify-content:space-between;
}
.lp-screen-name{font-family:var(--fm);font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--t3)}
.lp-screen-tag{
  font-family:var(--fm);font-size:10px;letter-spacing:.08em;
  color:var(--green);background:rgba(23,168,92,0.08);
  border:1px solid rgba(23,168,92,0.2);border-radius:3px;padding:2px 7px;
}
.lp-screen-body{padding:20px}

/* Screen mockups */
.lp-sm-kpis{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px}
.lp-sm-kpi{
  background:rgba(255,255,255,0.04);border:1px solid var(--border);
  border-radius:6px;padding:10px 12px;
}
.lp-sm-lbl{font-family:var(--fm);font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--t3);margin-bottom:4px}
.lp-sm-val{font-family:var(--fd);font-size:18px;font-weight:800;color:var(--t1);letter-spacing:-.02em}
.lp-sm-sub{font-size:10px;color:var(--green);margin-top:2px;font-family:var(--fm)}
.lp-sm-sub.dn{color:var(--red)}
.lp-sm-rows{display:flex;flex-direction:column;gap:6px}
.lp-sm-row{
  background:rgba(255,255,255,0.03);border:1px solid var(--border);
  border-radius:5px;padding:8px 10px;
  display:flex;align-items:center;justify-content:space-between;
}
.lp-sm-row-left{display:flex;flex-direction:column;gap:2px}
.lp-sm-row-name{font-size:11px;font-weight:600;color:var(--t1);font-family:var(--fd)}
.lp-sm-row-sub{font-size:10px;color:var(--t3);font-family:var(--fm)}
.lp-sm-row-right{text-align:right}
.lp-sm-row-val{font-family:var(--fd);font-size:12px;font-weight:700;color:var(--t1)}
.lp-sm-row-delta{font-size:10px;font-family:var(--fm)}
.lp-sm-bar-wrap{margin-top:4px;height:2px;background:rgba(255,255,255,0.06);border-radius:1px}
.lp-sm-bar{height:2px;background:var(--gold);border-radius:1px}

/* Debtors mockup */
.lp-sm-alert{
  background:rgba(196,64,64,0.08);border:1px solid rgba(196,64,64,0.2);
  border-radius:6px;padding:10px 12px;margin-bottom:10px;
}
.lp-sm-alert-title{font-family:var(--fm);font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--red);margin-bottom:4px}
.lp-sm-alert-val{font-family:var(--fd);font-size:20px;font-weight:800;color:var(--red)}
.lp-sm-alert-sub{font-size:10px;color:var(--t3);font-family:var(--fm)}
.lp-sm-ageing{display:flex;flex-direction:column;gap:5px}
.lp-sm-age-row{display:flex;align-items:center;gap:8px}
.lp-sm-age-lbl{font-family:var(--fm);font-size:9px;color:var(--t3);width:48px}
.lp-sm-age-bar-wrap{flex:1;height:6px;background:rgba(255,255,255,0.06);border-radius:3px}
.lp-sm-age-bar{height:6px;border-radius:3px}
.lp-sm-age-val{font-family:var(--fd);font-size:10px;font-weight:700;color:var(--t1);width:40px;text-align:right}

/* Stock mockup */
.lp-sm-stock-kpis{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin-bottom:10px}
.lp-sm-sk{
  background:rgba(255,255,255,0.04);border:1px solid var(--border);
  border-radius:5px;padding:8px 10px;
}
.lp-sm-sk-lbl{font-family:var(--fm);font-size:8px;letter-spacing:.08em;text-transform:uppercase;color:var(--t3);margin-bottom:3px}
.lp-sm-sk-val{font-family:var(--fd);font-size:13px;font-weight:800;color:var(--t1)}
.lp-sm-sk-val.warn{color:var(--amber)}

/* AI Advisor mockup */
.lp-sm-chat{display:flex;flex-direction:column;gap:8px}
.lp-sm-msg{
  padding:8px 10px;border-radius:6px;font-size:11px;line-height:1.5;
}
.lp-sm-msg.user{
  background:var(--gold-dim);border:1px solid var(--gold-b);
  color:var(--gold-l);align-self:flex-end;max-width:80%;font-family:var(--fm);
}
.lp-sm-msg.ai{
  background:rgba(255,255,255,0.04);border:1px solid var(--border);
  color:var(--t1);align-self:flex-start;max-width:90%;
}
.lp-sm-msg.ai strong{color:var(--gold-l)}

/* INTEGRATIONS */
.lp-integrations{
  padding:100px 52px;background:var(--void);
  border-bottom:1px solid var(--border);
}
.lp-int-inner{max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center}
.lp-int-grid{
  display:grid;grid-template-columns:repeat(3,1fr);gap:1px;
  background:var(--border);border:1px solid var(--border);border-radius:8px;overflow:hidden;
}
.lp-int-item{
  background:var(--void3);padding:24px 20px;text-align:center;
  transition:background .2s;
}
.lp-int-item:hover{background:rgba(196,146,42,0.03)}
.lp-int-icon{font-size:24px;margin-bottom:8px}
.lp-int-name{font-family:var(--fd);font-size:13px;font-weight:600;color:var(--t2);margin-bottom:3px}
.lp-int-type{font-family:var(--fm);font-size:10px;letter-spacing:.1em;color:var(--t3)}

/* PRICING */
.lp-pricing{
  padding:100px 52px;background:var(--void2);
  border-bottom:1px solid var(--border);
}
.lp-pricing-inner{max-width:1200px;margin:0 auto}
.lp-pricing-header{text-align:center;margin-bottom:64px}
.lp-pricing-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:var(--border);border:1px solid var(--border);border-radius:8px;overflow:hidden}
.lp-plan{
  background:var(--void2);padding:40px 32px;
  position:relative;
}
.lp-plan.featured{
  background:linear-gradient(160deg,rgba(196,146,42,0.08),rgba(196,146,42,0.02));
}
.lp-plan-badge{
  position:absolute;top:-1px;left:50%;transform:translateX(-50%);
  font-family:var(--fm);font-size:10px;letter-spacing:.12em;text-transform:uppercase;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));color:#05050a;
  padding:4px 16px;border-radius:0 0 8px 8px;
}
.lp-plan-name{font-family:var(--fd);font-size:14px;font-weight:600;color:var(--t3);letter-spacing:.08em;text-transform:uppercase;margin-bottom:16px}
.lp-plan-price{margin-bottom:8px}
.lp-plan-amount{font-family:var(--fd);font-size:40px;font-weight:800;color:var(--t1);letter-spacing:-.03em}
.lp-plan-currency{font-family:var(--fd);font-size:20px;font-weight:600;color:var(--t3);vertical-align:top;margin-top:8px;display:inline-block}
.lp-plan-period{font-size:14px;color:var(--t3);font-weight:300;font-family:var(--fb)}
.lp-plan-desc{font-size:14px;color:var(--t3);line-height:1.6;font-weight:300;margin-bottom:28px}
.lp-plan-features{display:flex;flex-direction:column;gap:10px;margin-bottom:32px}
.lp-pf{display:flex;align-items:flex-start;gap:10px;font-size:14px;color:var(--t2);line-height:1.5;font-weight:300}
.lp-pf::before{
  content:'✓';color:var(--green);font-size:12px;font-weight:700;
  flex-shrink:0;margin-top:1px;
}
.lp-pf.na{color:var(--t3)}
.lp-pf.na::before{content:'—';color:var(--t3);font-weight:400}
.lp-plan-cta{
  width:100%;padding:13px;border-radius:8px;
  font-family:var(--fb);font-size:14px;font-weight:600;cursor:pointer;
  transition:all .18s;
}
.lp-plan-cta.primary{
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  color:#05050a;border:none;
}
.lp-plan-cta.primary:hover{opacity:.88}
.lp-plan-cta.outline{
  background:transparent;color:var(--t1);
  border:1px solid var(--border2);
}
.lp-plan-cta.outline:hover{border-color:var(--gold-b);color:var(--gold-l)}
.lp-pricing-note{
  text-align:center;margin-top:32px;
  font-family:var(--fm);font-size:12px;letter-spacing:.08em;color:var(--t3);
}

/* TRUST */
.lp-trust{
  padding:80px 52px;background:var(--void);
  border-bottom:1px solid var(--border);
}
.lp-trust-inner{max-width:1200px;margin:0 auto;display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--border);border:1px solid var(--border);border-radius:8px;overflow:hidden}
.lp-trust-item{
  background:var(--void3);padding:32px 24px;text-align:center;
}
.lp-trust-icon{font-size:24px;margin-bottom:12px}
.lp-trust-title{font-family:var(--fd);font-size:14px;font-weight:700;color:var(--t1);margin-bottom:6px}
.lp-trust-text{font-size:13px;color:var(--t3);line-height:1.6;font-weight:300}

/* CONTACT */
.lp-contact{padding:100px 52px;background:var(--void2);border-bottom:1px solid var(--border)}
.lp-contact-inner{max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:start}
.lp-contact-bullets{margin-top:36px;display:flex;flex-direction:column;gap:12px}
.lp-contact-bullet{display:flex;align-items:flex-start;gap:12px;font-size:15px;color:var(--t2);line-height:1.6;font-weight:300}
.lp-contact-bullet::before{content:'';width:6px;height:6px;border-radius:50%;background:var(--gold);flex-shrink:0;margin-top:6px}
.lp-form{display:flex;flex-direction:column;gap:14px}
.lp-form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.lp-form-group{display:flex;flex-direction:column;gap:6px}
.lp-form-label{font-family:var(--fm);font-size:11px;letter-spacing:.15em;text-transform:uppercase;color:var(--t3)}
.lp-form-input{
  background:rgba(255,255,255,0.03);border:1px solid var(--border2);border-radius:6px;
  padding:10px 14px;font-family:var(--fb);font-size:14px;color:var(--t1);
  outline:none;transition:border-color .18s;width:100%;font-weight:300;
}
.lp-form-input::placeholder{color:var(--t3)}
.lp-form-input:focus{border-color:var(--gold-b)}
.lp-form-textarea{min-height:90px;resize:vertical}
.lp-form-submit{
  font-family:var(--fb);font-size:14px;font-weight:600;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  color:#05050a;border:none;border-radius:8px;padding:14px 28px;
  cursor:pointer;transition:opacity .18s;
}
.lp-form-submit:hover{opacity:.88}
.lp-form-submit:disabled{opacity:.5;cursor:not-allowed}
.lp-form-success{background:rgba(23,168,92,0.08);border:1px solid rgba(23,168,92,0.2);border-radius:6px;padding:24px;text-align:center}
.lp-form-success-title{font-family:var(--fd);font-size:18px;color:var(--t1);margin-bottom:8px;font-weight:600}
.lp-form-success-sub{font-size:14px;color:var(--t3)}
.lp-form-error{font-family:var(--fm);font-size:12px;color:var(--red);padding:10px 14px;background:rgba(196,64,64,0.08);border:1px solid rgba(196,64,64,0.2);border-radius:4px}

/* CTA BAND */
.lp-ctaband{
  padding:80px 52px;
  background:linear-gradient(135deg,rgba(196,146,42,0.07),rgba(196,146,42,0.02));
  border-top:1px solid var(--gold-b);border-bottom:1px solid var(--border);text-align:center;
}
.lp-ctaband-title{font-family:var(--fd);font-size:clamp(28px,3.5vw,44px);font-weight:800;letter-spacing:-.03em;color:var(--t1);line-height:1.1;margin-bottom:16px}
.lp-ctaband-title em{font-style:italic;color:var(--gold-l)}
.lp-ctaband-sub{font-size:16px;color:var(--t2);font-weight:300;max-width:460px;margin:0 auto 36px;line-height:1.7}
.lp-ctaband-actions{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-bottom:28px}
.lp-ctaband-badges{display:flex;gap:20px;justify-content:center;flex-wrap:wrap}
.lp-badge{font-family:var(--fm);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--t3);display:flex;align-items:center;gap:6px}
.lp-badge::before{content:'✓';color:var(--green)}

/* FOOTER */
.lp-footer{background:var(--void2);border-top:1px solid var(--border);padding:56px 52px 32px}
.lp-footer-inner{max-width:1200px;margin:0 auto}
.lp-footer-top{display:grid;grid-template-columns:1.5fr 1fr 1fr 1fr;gap:48px;margin-bottom:48px}
.lp-footer-brand-text{font-size:14px;color:var(--t3);line-height:1.65;margin-top:12px;font-weight:300;max-width:220px}
.lp-footer-col-title{font-family:var(--fm);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--t3);margin-bottom:16px}
.lp-footer-links{list-style:none;display:flex;flex-direction:column;gap:10px}
.lp-footer-links li a{font-size:14px;color:var(--t3);text-decoration:none;cursor:pointer;transition:color .18s;font-weight:300}
.lp-footer-links li a:hover{color:var(--t1)}
.lp-footer-bottom{border-top:1px solid var(--border);padding-top:24px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px}
.lp-footer-copy{font-family:var(--fm);font-size:11px;letter-spacing:.1em;color:var(--t3)}
.lp-footer-legal{display:flex;gap:20px}
.lp-footer-legal a{font-size:12px;color:var(--t3);text-decoration:none;cursor:pointer;transition:color .18s}
.lp-footer-legal a:hover{color:var(--t1)}

/* PAIN SECTION */
.lp-pain{
  padding:100px 52px;background:var(--void);
  border-bottom:1px solid var(--border);
}
.lp-pain-inner{max-width:1200px;margin:0 auto}
.lp-pain-header{margin-bottom:64px}
.lp-pain-grid{
  display:grid;grid-template-columns:repeat(2,1fr);gap:1px;
  background:var(--border);border:1px solid var(--border);border-radius:8px;overflow:hidden;
}
.lp-pain-item{
  background:var(--void);padding:40px 36px;
  position:relative;
}
.lp-pain-num{
  font-family:var(--fd);font-size:13px;font-weight:800;
  color:var(--gold-b);letter-spacing:.08em;margin-bottom:16px;
}
.lp-pain-title{font-family:var(--fd);font-size:18px;font-weight:700;color:var(--t1);margin-bottom:10px;letter-spacing:-.01em}
.lp-pain-text{font-size:15px;color:var(--t2);line-height:1.7;font-weight:300}
.lp-pain-text strong{color:var(--t1);font-weight:500}

/* WHAT YOU CONTROL */
.lp-control{
  padding:100px 52px;
  background:linear-gradient(180deg,var(--void2),var(--void));
  border-bottom:1px solid var(--border);
}
.lp-control-inner{max-width:1200px;margin:0 auto}
.lp-control-header{text-align:center;margin-bottom:64px}
.lp-control-grid{
  display:grid;grid-template-columns:repeat(3,1fr);gap:24px;
}
.lp-control-card{
  background:var(--void3);border:1px solid var(--border);
  border-radius:10px;padding:32px 28px;
  transition:border-color .2s,transform .2s;
}
.lp-control-card:hover{border-color:var(--gold-b);transform:translateY(-2px)}
.lp-control-icon{font-size:28px;margin-bottom:16px}
.lp-control-title{font-family:var(--fd);font-size:17px;font-weight:700;color:var(--t1);margin-bottom:8px;letter-spacing:-.01em}
.lp-control-outcome{
  font-family:var(--fm);font-size:11px;letter-spacing:.1em;text-transform:uppercase;
  color:var(--gold);margin-bottom:12px;
}
.lp-control-text{font-size:14px;color:var(--t2);line-height:1.65;font-weight:300}

/* USE CASES */
.lp-usecases{
  padding:100px 52px;background:var(--void2);
  border-bottom:1px solid var(--border);
}
.lp-usecases-inner{max-width:1200px;margin:0 auto}
.lp-usecases-header{text-align:center;margin-bottom:64px}
.lp-usecases-grid{
  display:grid;grid-template-columns:repeat(2,1fr);gap:24px;
}
.lp-usecase{
  background:var(--void3);border:1px solid var(--border);
  border-radius:10px;padding:32px 28px;
  display:flex;gap:20px;align-items:flex-start;
  transition:border-color .2s;
}
.lp-usecase:hover{border-color:var(--gold-b)}
.lp-usecase-icon{
  font-size:28px;flex-shrink:0;
  width:52px;height:52px;border-radius:8px;
  background:var(--gold-dim);border:1px solid var(--gold-b);
  display:flex;align-items:center;justify-content:center;
}
.lp-usecase-content{}
.lp-usecase-industry{font-family:var(--fm);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--gold);margin-bottom:6px}
.lp-usecase-title{font-family:var(--fd);font-size:16px;font-weight:700;color:var(--t1);margin-bottom:8px;letter-spacing:-.01em}
.lp-usecase-text{font-size:14px;color:var(--t2);line-height:1.65;font-weight:300}

/* ANIMATED DEMO */
.lp-demo{
  padding:100px 52px;background:var(--void2);
  border-bottom:1px solid var(--border);
  overflow:hidden;
}
.lp-demo-inner{max-width:1200px;margin:0 auto}
.lp-demo-header{text-align:center;margin-bottom:64px}
.lp-demo-stage{
  display:flex;gap:64px;align-items:center;
  justify-content:center;
}
.lp-demo-left{
  flex:1;max-width:440px;
}
.lp-demo-tabs{
  display:flex;flex-direction:column;gap:4px;margin-bottom:32px;
}
.lp-demo-tab{
  display:flex;align-items:center;gap:14px;
  padding:14px 16px;border-radius:8px;cursor:pointer;
  border:1px solid transparent;transition:all .25s;
}
.lp-demo-tab.active{
  background:var(--gold-dim);border-color:var(--gold-b);
}
.lp-demo-tab:hover:not(.active){background:rgba(255,255,255,0.03)}
.lp-demo-tab-icon{
  width:36px;height:36px;border-radius:6px;
  display:flex;align-items:center;justify-content:center;
  font-size:16px;flex-shrink:0;
  background:rgba(255,255,255,0.04);border:1px solid var(--border);
  transition:all .25s;
}
.lp-demo-tab.active .lp-demo-tab-icon{
  background:var(--gold-dim);border-color:var(--gold-b);
}
.lp-demo-tab-content{}
.lp-demo-tab-title{font-family:var(--fd);font-size:15px;font-weight:700;color:var(--t2);transition:color .25s;letter-spacing:-.01em}
.lp-demo-tab.active .lp-demo-tab-title{color:var(--t1)}
.lp-demo-tab-desc{font-size:13px;color:var(--t3);margin-top:2px;line-height:1.5;font-weight:300}
.lp-demo-progress{
  height:2px;background:rgba(255,255,255,0.06);border-radius:1px;
  margin-top:24px;overflow:hidden;
}
.lp-demo-progress-bar{
  height:2px;background:linear-gradient(90deg,var(--gold-l),var(--gold));
  border-radius:1px;
  transition:width 0.1s linear;
}
.lp-demo-right{
  width:280px;flex-shrink:0;position:relative;
}
.lp-demo-phone{
  width:280px;background:#0d0d14;border-radius:36px;
  border:2px solid rgba(255,255,255,0.14);
  box-shadow:0 40px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.04),inset 0 1px 0 rgba(255,255,255,0.06);
  overflow:hidden;
}
.lp-demo-phone-notch{
  height:28px;background:#05050a;display:flex;align-items:center;justify-content:center;
}
.lp-demo-notch-pill{width:80px;height:14px;background:#111;border-radius:7px}
.lp-demo-phone-topbar{
  background:#0a0a10;padding:8px 12px;
  display:flex;align-items:center;justify-content:space-between;
}
.lp-demo-logo-wrap{display:flex;align-items:center;gap:6px}
.lp-demo-hex{
  width:20px;height:20px;
  background:linear-gradient(135deg,#dba93a,#c4922a);
  clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);
  display:flex;align-items:center;justify-content:center;
}
.lp-demo-hex span{font-size:6px;font-weight:800;color:#0a0a10;font-family:'Syne',sans-serif}
.lp-demo-brand{font-size:10px;font-weight:700;color:#eeeef8;font-family:'Syne',sans-serif}
.lp-demo-brand sup{font-size:6px;color:#dba93a}
.lp-demo-avatar{width:20px;height:20px;border-radius:50%;background:linear-gradient(135deg,#dba93a,#c4922a);display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:800;color:#0a0a10;font-family:'Syne',sans-serif}
.lp-demo-period-bar{
  display:flex;gap:2px;padding:5px 8px;background:#0a0a10;
}
.lp-demo-pp{font-size:7px;color:#50506a;padding:3px 5px;border-radius:3px;font-family:'JetBrains Mono',monospace;letter-spacing:.05em}
.lp-demo-pp.act{background:#c4922a;color:#05050a;font-weight:700}
.lp-demo-screen{background:#f0f2f5;min-height:420px;position:relative;overflow:hidden}
.lp-demo-slide{
  position:absolute;inset:0;
  opacity:0;transform:translateY(12px);
  transition:opacity .4s ease,transform .4s ease;
  pointer-events:none;
}
.lp-demo-slide.active{
  opacity:1;transform:translateY(0);
  pointer-events:auto;
}
.lp-demo-glow{
  position:absolute;width:360px;height:360px;border-radius:50%;
  background:radial-gradient(circle,rgba(196,146,42,0.12),transparent 70%);
  top:50%;left:50%;transform:translate(-50%,-50%);
  pointer-events:none;z-index:0;
}

/* Slide content reuses existing phone CSS classes */
.lp-demo-section-tag{
  padding:6px 12px;background:#0a0a10;
  font-size:7px;color:var(--gold, #c4922a);font-family:'JetBrains Mono',monospace;
  letter-spacing:.12em;text-transform:uppercase;
  display:flex;align-items:center;justify-content:space-between;
}
.lp-demo-live{font-size:7px;color:#17a85c;font-family:'JetBrains Mono',monospace}

@media(max-width:900px){
  .lp-demo{padding:60px 20px}
  .lp-demo-stage{flex-direction:column;gap:40px}
  .lp-demo-left{max-width:100%;width:100%}
  .lp-demo-right{width:100%;display:flex;justify-content:center}
  .lp-demo-phone{width:260px}
  .lp-demo-screen{min-height:380px}
}

/* REVEAL */
.lp-reveal{opacity:0;transform:translateY(20px);transition:opacity .6s ease,transform .6s ease}
.lp-reveal.lp-visible{opacity:1;transform:translateY(0)}

/* MOBILE */
@media(max-width:900px){
  .lp-nav{padding:0 20px}
  .lp-nav-links{display:none}
  .lp-hamburger{display:block}
  .lp-nav-right .lp-btn-ghost{display:none}
  .lp-mobile-menu{
    display:none;position:fixed;top:64px;left:0;right:0;
    background:rgba(5,5,10,0.97);backdrop-filter:blur(24px);
    border-bottom:1px solid var(--border);
    flex-direction:column;padding:16px;gap:4px;z-index:99;
  }
  .lp-mobile-menu.open{display:flex}
  .lp-mobile-menu a{
    font-size:15px;color:var(--t2);text-decoration:none;
    padding:12px 8px;border-bottom:1px solid var(--border);cursor:pointer;
  }
  .lp-mobile-menu a:last-child{border-bottom:none}
  .lp-hero{padding:100px 16px 60px}
  .lp-hero-inner{grid-template-columns:1fr;gap:40px}
  .lp-h1{font-size:clamp(26px,8vw,36px);letter-spacing:-.01em}
  .lp-hero-sub{font-size:15px}
  .lp-hero-content{min-width:0;overflow:hidden}
  .lp-phone2{display:none}
  .lp-hiw{padding:60px 20px}
  .lp-hiw-steps{grid-template-columns:1fr;gap:40px}
  .lp-hiw-steps::before{display:none}
  .lp-step{padding:0}
  .lp-pain{padding:60px 20px}
  .lp-pain-grid{grid-template-columns:1fr}
  .lp-pain-item{padding:28px 20px}
  .lp-control{padding:60px 20px}
  .lp-control-grid{grid-template-columns:1fr}
  .lp-usecases{padding:60px 20px}
  .lp-usecases-grid{grid-template-columns:1fr}
  .lp-modules{padding:60px 20px}
  .lp-modules-grid{grid-template-columns:1fr}
  .lp-module{padding:24px 20px}
  .lp-screens{padding:60px 20px}
  .lp-screens-grid{grid-template-columns:1fr}
  .lp-screen-body{padding:14px}
  .lp-integrations{padding:60px 20px}
  .lp-int-inner{grid-template-columns:1fr;gap:40px}
  .lp-pricing{padding:60px 20px}
  .lp-pricing-grid{grid-template-columns:1fr}
  .lp-plan{padding:28px 20px}
  .lp-trust{padding:60px 20px}
  .lp-trust-inner{grid-template-columns:1fr 1fr}
  .lp-contact{padding:60px 20px}
  .lp-contact-inner{grid-template-columns:1fr;gap:40px}
  .lp-ctaband{padding:60px 20px}
  .lp-footer{padding:40px 20px 24px}
  .lp-footer-top{grid-template-columns:1fr 1fr;gap:32px}
  .lp-form-row{grid-template-columns:1fr}
}
@media(max-width:500px){
  .lp-trust-inner{grid-template-columns:1fr}
  .lp-footer-top{grid-template-columns:1fr}
  .lp-int-grid{grid-template-columns:1fr 1fr}
  .lp-hero-proof{gap:16px}
  .lp-proof-val{font-size:20px}
  .lp-hero-sub{font-size:15px}
  .lp-ctaband-badges{gap:12px}
  .lp-hero-actions{flex-direction:column}
  .lp-btn-primary,.lp-btn-outline{width:100%;text-align:center}
}
`;

const FORM_IDLE = 'idle', FORM_SENDING = 'sending', FORM_SUCCESS = 'success', FORM_ERROR = 'error';

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDemo, setActiveDemo] = useState(0);
  const [demoProgress, setDemoProgress] = useState(0);
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', company:'', phone:'', industry:'', branches:'', system:'', message:'' });
  const [formState, setFormState] = useState(FORM_IDLE);
  const [formError, setFormError] = useState('');
  const updateForm = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.firstName || !form.email || !form.company) { setFormError('Please fill in First Name, Email and Company Name.'); return; }
    setFormError(''); setFormState(FORM_SENDING);
    try {
      await axios.post(`${BASE}/sendDemoRequest`, { firstName:form.firstName, lastName:form.lastName, email:form.email, company:form.company, industry:form.industry, branches:form.branches, system:form.system, message:form.message });
      setFormState(FORM_SUCCESS);
    } catch { setFormState(FORM_ERROR); setFormError('Something went wrong. Please email us at info@insightpro.co.za'); }
  };

  useEffect(() => {
    const el = document.createElement('style'); el.id = 'lp-styles-v3';
    if (!document.getElementById('lp-styles-v3')) document.head.appendChild(el);
    el.textContent = css;
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('lp-visible'); }), { threshold: 0.06 });
    document.querySelectorAll('.lp-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Auto-cycle demo tabs
  useEffect(() => {
    const DURATION = 5000;
    const TICK = 50;
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += TICK;
      setDemoProgress(Math.min((elapsed / DURATION) * 100, 100));
      if (elapsed >= DURATION) {
        elapsed = 0;
        setDemoProgress(0);
        setActiveDemo(prev => (prev + 1) % 4);
      }
    }, TICK);
    return () => clearInterval(timer);
  }, [activeDemo]);

  const scrollTo = id => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior:'smooth', block:'start' }); };

  return (
    <div className="lp">
      <style>{`@keyframes ribbonScroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-logo" onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}>
          <div className="lp-logo-hex"><span>IP</span></div>
          <div className="lp-brand">InsightPro<sup>™</sup></div>
        </div>
        <div className="lp-nav-links">
          <a onClick={() => scrollTo('problems')}>The Problem</a>
          <a onClick={() => scrollTo('platform')}>Live Demo</a>
          <a onClick={() => scrollTo('modules')}>Platform</a>
          <a onClick={() => scrollTo('pricing')}>Pricing</a>
        </div>
        <div className="lp-nav-right">
          <button className="lp-btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
          <button className="lp-btn-gold" onClick={() => scrollTo('contact')}>Book a Demo</button>
        </div>
        <button className="lp-hamburger" onClick={() => setMenuOpen(o => !o)}>{menuOpen ? '✕' : '☰'}</button>
      </nav>

      <div className={`lp-mobile-menu${menuOpen ? ' open' : ''}`}>
        <a onClick={() => scrollTo('problems')}>The Problem</a>
        <a onClick={() => scrollTo('platform')}>Live Demo</a>
        <a onClick={() => scrollTo('modules')}>Platform</a>
        <a onClick={() => scrollTo('pricing')}>Pricing</a>
        <a onClick={() => navigate('/login')}>Sign In</a>
        <a onClick={() => scrollTo('contact')}>Book a Demo</a>
      </div>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-content">
            <div className="lp-eyebrow">Retail Control System</div>
            <h1 className="lp-h1">
              Identify underperforming stores,<br />
              dead stock and cash flow risks —<br />
              <em>before they hit your bottom line.</em>
            </h1>
            <p className="lp-hero-sub">
              InsightPro is a retail intelligence and control system built for multi-branch operations. Connect your POS and ERP systems once — and see everything that matters, live, from any device.
            </p>
            <div className="lp-hero-actions">
              <button className="lp-btn-primary" onClick={() => scrollTo('contact')}>Book a Live Demo →</button>
              <button className="lp-btn-outline" onClick={() => scrollTo('how-it-works')}>See How It Works</button>
            </div>
            <div className="lp-hero-proof">
              {[['4','click','drill to product level'],['6','modules','out of the box'],['60','second','morning brief']].map(([v, s, l]) => (
                <div className="lp-proof-item" key={l}>
                  <div className="lp-proof-val">{v}<span> {s}</span></div>
                  <div className="lp-proof-lbl">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone mockup */}
          <div className="lp-phone-wrap">
            <div className="lp-phone-glow" />
            <div className="lp-phone">
              <div className="lp-phone-notch"><div className="lp-phone-notch-bar" /></div>
              <div className="lp-phone-screen">
                <div className="lp-phone-topbar">
                  <div className="lp-phone-topbar-logo">
                    <div className="lp-phone-topbar-hex"><span>IP</span></div>
                    <div className="lp-phone-topbar-brand">InsightPro<sup>™</sup></div>
                  </div>
                  <div style={{ width:20, height:20, borderRadius:'50%', background:'linear-gradient(135deg,#dba93a,#c4922a)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#05050a', fontFamily:'Syne,sans-serif' }}>U</div>
                </div>
                <div className="lp-phone-period">
                  {['TODAY','YEST','WTD','MTD','QTD','YTD'].map((t,i) => (
                    <div key={t} className={`lp-pp${i===3?' act':''}`}>{t}</div>
                  ))}
                </div>
                <div className="lp-phone-kpis">
                  <div className="lp-phone-kpi">
                    <div className="lp-pk-lbl">Total Revenue</div>
                    <div className="lp-pk-val">R 21.6M</div>
                    <div className="lp-pk-sub">↑ +9.4% vs prior</div>
                  </div>
                  <div className="lp-phone-kpi">
                    <div className="lp-pk-lbl">Gross Margin</div>
                    <div className="lp-pk-val">74.2%</div>
                    <div className="lp-pk-sub" style={{color:'#888'}}>excl. overhead</div>
                  </div>
                  <div className="lp-phone-kpi">
                    <div className="lp-pk-lbl">Units Sold</div>
                    <div className="lp-pk-val">12 480</div>
                    <div className="lp-pk-sub">current period</div>
                  </div>
                  <div className="lp-phone-kpi">
                    <div className="lp-pk-lbl">Top Branch</div>
                    <div className="lp-pk-val" style={{fontSize:11}}>CITY CENTRAL</div>
                    <div className="lp-pk-sub">↑ R 3.1M</div>
                  </div>
                </div>
                <div className="lp-phone-section-lbl">BRANCH PERFORMANCE</div>
                <div className="lp-phone-branch-rows">
                  {[
                    { n:'01', name:'City Central',  val:'R 2.4M', delta:'+14.2%', up:true,  w:90 },
                    { n:'02', name:'Gateway',        val:'R 2.2M', delta:'+11.2%', up:true,  w:80 },
                    { n:'03', name:'Online Store',   val:'R 1.8M', delta:'+19.3%', up:true,  w:62 },
                    { n:'04', name:'Southgate',      val:'R 1.5M', delta:'-4.1%',  up:false, w:55 },
                  ].map(b => (
                    <div className="lp-pbr" key={b.n}>
                      <div className="lp-pbr-num">{b.n}</div>
                      <div style={{flex:1}}>
                        <div className="lp-pbr-name">{b.name}</div>
                        <div className="lp-pbr-bar" style={{ width:`${b.w}%` }} />
                      </div>
                      <div className="lp-pbr-val">{b.val}</div>
                      <div className="lp-pbr-grow" style={{ color: b.up ? '#17a85c':'#c44040' }}>{b.delta}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Second phone - stock */}
            <div className="lp-phone2">
              <div className="lp-phone2-screen">
                <div className="lp-phone2-header">
                  <div className="lp-phone2-title">Stock & Inventory</div>
                  <div className="lp-phone2-live">● LIVE</div>
                </div>
                <div className="lp-phone2-rows">
                  {[
                    { name:'Total Value',   val:'R 18.4M', delta:'+3.8%',  up:true },
                    { name:'Days on Hand',  val:'38 days',  delta:'healthy', up:true },
                    { name:'Active Lines',  val:'1 240',    delta:'items',  up:true },
                    { name:'Zero Stock',    val:'54',       delta:'lines',  up:false },
                  ].map(r => (
                    <div className="lp-p2r" key={r.name}>
                      <div className="lp-p2r-name">{r.name}</div>
                      <div style={{textAlign:'right'}}>
                        <div className="lp-p2r-val">{r.val}</div>
                        <div className="lp-p2r-delta" style={{ color: r.up ? '#17a85c':'#c44040', fontSize:7 }}>{r.delta}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RIBBON */}
      <div className="lp-ribbon">
        <div className="lp-ribbon-track">
          {['Multi-Branch Retail','Wholesale','FMCG','Furniture & Appliances','Manufacturing','Power BI Gateway','IQ Retail Integration','Vision Integration','Real-Time KPIs','POPIA Compliant','AI Advisor','Mobile-First',
            'Multi-Branch Retail','Wholesale','FMCG','Furniture & Appliances','Manufacturing','Power BI Gateway','IQ Retail Integration','Vision Integration','Real-Time KPIs','POPIA Compliant','AI Advisor','Mobile-First'].map((item,i) => (
            <div className="lp-ribbon-item" key={i}><div className="lp-ribbon-dot" />{item}</div>
          ))}
        </div>
      </div>

      {/* PAIN SECTION */}
      <section className="lp-pain" id="problems">
        <div className="lp-pain-inner">
          <div className="lp-pain-header lp-reveal">
            <div className="lp-section-eyebrow" style={{justifyContent:'flex-start'}}>The Problem</div>
            <h2 className="lp-section-title">Most retail businesses<br /><em>are flying blind.</em></h2>
            <p className="lp-section-sub">Not because the data doesn't exist — but because it's scattered across systems, branches and spreadsheets with no way to see it all at once.</p>
          </div>
          <div className="lp-pain-grid lp-reveal">
            {[
              { n:'01', title:'You find out too late', text:"Stock problems, margin erosion and debtor blowouts surface at month-end — when the damage is already done. You need to see <strong>today's numbers</strong>, not last month's report." },
              { n:'02', title:'No visibility across branches', text:"Getting a consolidated view means calling managers, waiting for emails or building it yourself in Excel. By the time you have the numbers, the decisions have already been made." },
              { n:'03', title:'Dead stock ties up your cash', text:"Overstock sitting in the wrong branch. Lines that haven't moved in 90 days. <strong>You can't manage what you can't see</strong> — and most retailers can't see their stock position clearly." },
              { n:'04', title:'Debtors spiral out of control', text:"A debtor book that's 40–50% overdue is common in retail. Without daily visibility, small balances become large write-offs. You need to know who owes what — and for how long." },
            ].map(item => (
              <div className="lp-pain-item" key={item.n}>
                <div className="lp-pain-num">{item.n}</div>
                <div className="lp-pain-title">{item.title}</div>
                <div className="lp-pain-text" dangerouslySetInnerHTML={{__html: item.text}} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU CONTROL */}
      <section className="lp-control">
        <div className="lp-control-inner">
          <div className="lp-control-header lp-reveal">
            <div className="lp-section-eyebrow">What You Control</div>
            <h2 className="lp-section-title">Every number that<br /><em>drives your business.</em></h2>
            <p className="lp-section-sub">InsightPro doesn't just show you data. It gives you control over the metrics that determine whether your retail operation makes or loses money.</p>
          </div>
          <div className="lp-control-grid lp-reveal">
            {[
              { icon:'💰', title:'Sales & Revenue', outcome:'Know before month-end', text:"Track revenue by branch, department and product in real time. Spot underperforming stores the day it happens — not three weeks later." },
              { icon:'📦', title:'Stock & Inventory', outcome:'Stop losing money to dead stock', text:"See exactly what is on hand at every branch. Identify slow movers, zero stock lines and overstocked items before they become a cash flow problem." },
              { icon:'👥', title:'Debtors & Credit', outcome:'Protect your cash flow', text:"Full ageing analysis from Current to 180+ days. Know which accounts are at risk, who is on hold, and where your biggest exposure sits." },
              { icon:'🏬', title:'Branch Performance', outcome:'Find the weak links', text:"Compare every store side by side. Revenue, margin, units, growth — all in one view. Identify which branches are dragging the group down." },
              { icon:'📊', title:'Gross Margin', outcome:'Track profitability daily', text:"Margin by branch, by department, by product. See where you are making money and where margin is compressing before it hits your bottom line." },
              { icon:'🤖', title:'AI-Powered Insight', outcome:'Ask any question, get a real answer', text:"Type a question about your business in plain language. The AI Advisor has full context — all branches, all debtors, all products — and responds instantly." },
            ].map(c => (
              <div className="lp-control-card" key={c.title}>
                <div className="lp-control-icon">{c.icon}</div>
                <div className="lp-control-outcome">{c.outcome}</div>
                <div className="lp-control-title">{c.title}</div>
                <div className="lp-control-text">{c.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-hiw" id="how-it-works">
        <div className="lp-hiw-inner">
          <div className="lp-hiw-header lp-reveal">
            <div className="lp-section-eyebrow">How It Works</div>
            <h2 className="lp-section-title">Up and running<br /><em>in days, not months.</em></h2>
            <p className="lp-section-sub">No custom development. No data migration. No disruption to your team. We connect to your existing systems and your dashboard goes live.</p>
          </div>
          <div className="lp-hiw-steps">
            {[
              { n:'1', title:'Connect Your Systems', text:"We install a lightweight data gateway at your premises. It connects directly to your IQ Retail, Vision or ERP system. Your raw data never leaves your site — only aggregated insights go to the cloud.", tags:['IQ Retail','Vision Software','ERP Systems','Excel','POS Systems'] },
              { n:'2', title:'Data Syncs Automatically', text:"Your data refreshes on a scheduled cycle — overnight by default, or more frequently. No manual exports. No spreadsheet uploads. No phone calls to branch managers asking for numbers.", tags:['Nightly sync','Real-time queries','Cloud-based','POPIA compliant'] },
              { n:'3', title:'Control Your Business Daily', text:"Log in from your phone, tablet or laptop. See every branch, every metric, every morning. Drill from total revenue down to a specific product at a specific store in four clicks.", tags:['Mobile-first','Multi-branch','Drill-down','AI Advisor'] },
            ].map(s => (
              <div className="lp-step lp-reveal" key={s.n}>
                <div className="lp-step-num">{s.n}</div>
                <div className="lp-step-title">{s.title}</div>
                <div className="lp-step-text">{s.text}</div>
                <div className="lp-step-tech">
                  {s.tags.map(t => <div className="lp-tech-pill" key={t}>{t}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ANIMATED DEMO */}
      <section className="lp-demo" id="platform">
        <div className="lp-demo-inner">
          <div className="lp-demo-header lp-reveal">
            <div className="lp-section-eyebrow">Live Product Demo</div>
            <h2 className="lp-section-title">See every part of<br /><em>your business, live.</em></h2>
            <p className="lp-section-sub">Four modules. One platform. Everything updates in real time — no page refresh, no waiting.</p>
          </div>
          <div className="lp-demo-stage lp-reveal">

            {/* Tab selector */}
            <div className="lp-demo-left">
              <div className="lp-demo-tabs">
                {[
                  { icon:'📊', title:'Executive Overview', desc:'Revenue, margin and branch ranking at a glance — for any time period.' },
                  { icon:'💰', title:'Sales & Revenue', desc:'Drill from total revenue down to individual product in four clicks.' },
                  { icon:'📦', title:'Stock & Inventory', desc:'On-hand quantities per branch, slow movers and zero stock alerts.' },
                  { icon:'👥', title:'Debtors & Credit', desc:'Full ageing analysis — who owes you, how much, and for how long.' },
                ].map((tab, i) => (
                  <div
                    key={tab.title}
                    className={`lp-demo-tab${activeDemo === i ? ' active' : ''}`}
                    onClick={() => { setActiveDemo(i); setDemoProgress(0); }}
                  >
                    <div className="lp-demo-tab-icon">{tab.icon}</div>
                    <div className="lp-demo-tab-content">
                      <div className="lp-demo-tab-title">{tab.title}</div>
                      <div className="lp-demo-tab-desc">{tab.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="lp-demo-progress">
                <div className="lp-demo-progress-bar" style={{ width: `${demoProgress}%` }} />
              </div>
            </div>

            {/* Phone with animated screens */}
            <div className="lp-demo-right">
              <div className="lp-demo-glow" />
              <div className="lp-demo-phone">
                <div className="lp-demo-phone-notch"><div className="lp-demo-notch-pill" /></div>
                {/* Topbar */}
                <div className="lp-demo-phone-topbar">
                  <div className="lp-demo-logo-wrap">
                    <div className="lp-demo-hex"><span>IP</span></div>
                    <div className="lp-demo-brand">InsightPro<sup>™</sup></div>
                  </div>
                  <div className="lp-demo-avatar">U</div>
                </div>
                {/* Period bar */}
                <div className="lp-demo-period-bar">
                  {['TODAY','YEST','WTD','MTD','QTD','YTD'].map((t,i) => (
                    <div key={t} className={`lp-demo-pp${i===3?' act':''}`}>{t}</div>
                  ))}
                </div>
                {/* Screen slides */}
                <div className="lp-demo-screen">

                  {/* Slide 0: Executive Overview */}
                  <div className={`lp-demo-slide${activeDemo===0?' active':''}`}>
                    <div className="lp-demo-section-tag">
                      <span>KEY PERFORMANCE INDICATORS</span>
                      <span className="lp-demo-live">● LIVE</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,padding:8}}>
                      {[
                        {l:'TOTAL REVENUE',v:'R 21.6M',s:'↑ +9.4% vs prior',sc:'#17a85c'},
                        {l:'GROSS MARGIN',v:'74.2%',s:'excl. overhead',sc:'#888'},
                        {l:'UNITS SOLD',v:'12 480',s:'current period',sc:'#888'},
                        {l:'TOP BRANCH',v:'CITY CENTRAL',s:'↑ R 2.4M',sc:'#17a85c'},
                      ].map(k => (
                        <div key={k.l} style={{background:'#fff',borderRadius:8,padding:'8px 10px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                          <div style={{fontSize:7,letterSpacing:'.1em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace",marginBottom:3}}>{k.l}</div>
                          <div style={{fontSize:k.v.length>7?11:13,fontWeight:800,color:'#0a0a10',fontFamily:"'Syne',sans-serif",letterSpacing:'-.02em'}}>{k.v}</div>
                          <div style={{fontSize:7,color:k.sc,marginTop:2,fontFamily:"'JetBrains Mono',monospace"}}>{k.s}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'0 8px 4px',fontSize:7,letterSpacing:'.12em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>BRANCH PERFORMANCE</div>
                    <div style={{padding:'0 8px 8px',display:'flex',flexDirection:'column',gap:5}}>
                      {[
                        {n:'01',name:'City Central', val:'R 2.4M',delta:'+14.2%',up:true,w:90},
                        {n:'02',name:'Gateway',      val:'R 2.2M',delta:'+9.8%',up:true,w:78},
                        {n:'03',name:'Online Store', val:'R 1.8M',delta:'+19.3%',up:true,w:60},
                        {n:'04',name:'Southgate',    val:'R 1.5M',delta:'-4.1%',up:false,w:55},
                      ].map(b => (
                        <div key={b.n} style={{background:'#fff',borderRadius:6,padding:'6px 8px',display:'flex',alignItems:'center',gap:6,boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}>
                          <div style={{fontSize:7,color:'#aaa',width:14,fontFamily:"'JetBrains Mono',monospace"}}>{b.n}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:8,fontWeight:600,color:'#0a0a10',fontFamily:"'Instrument Sans',sans-serif"}}>{b.name}</div>
                            <div style={{height:2,background:'#c4922a',borderRadius:1,marginTop:3,width:`${b.w}%`}} />
                          </div>
                          <div style={{fontSize:8,fontWeight:700,color:'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{b.val}</div>
                          <div style={{fontSize:7,fontFamily:"'JetBrains Mono',monospace",color:b.up?'#17a85c':'#c44040',width:32,textAlign:'right'}}>{b.delta}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Slide 1: Sales & Revenue */}
                  <div className={`lp-demo-slide${activeDemo===1?' active':''}`}>
                    <div className="lp-demo-section-tag">
                      <span>SALES & REVENUE</span>
                      <span className="lp-demo-live">● LIVE</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,padding:8}}>
                      {[
                        {l:'TOTAL REVENUE',v:'R 21.6M',s:'+9.4% vs last year',sc:'#17a85c'},
                        {l:'GROSS MARGIN',v:'74.2%',s:'excl. overhead',sc:'#888'},
                      ].map(k => (
                        <div key={k.l} style={{background:'#fff',borderRadius:8,padding:'8px 10px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                          <div style={{fontSize:7,letterSpacing:'.1em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace",marginBottom:3}}>{k.l}</div>
                          <div style={{fontSize:12,fontWeight:800,color:'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{k.v}</div>
                          <div style={{fontSize:7,color:k.sc,marginTop:2,fontFamily:"'JetBrains Mono',monospace"}}>{k.s}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'0 8px 4px',fontSize:7,letterSpacing:'.12em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>TOP DEPARTMENTS</div>
                    <div style={{padding:'0 8px 8px',display:'flex',flexDirection:'column',gap:4}}>
                      {[
                        {n:'01',name:'Home & Living',       val:'R 5.8M', delta:'+17.2%',up:true},
                        {n:'02',name:'Furniture',           val:'R 4.2M', delta:'+22.1%',up:true},
                        {n:'03',name:'Electronics',         val:'R 3.6M', delta:'+14.3%',up:true},
                        {n:'04',name:'Outdoor & Garden',    val:'R 3.0M', delta:'+8.7%', up:true},
                        {n:'05',name:'Kitchen & Dining',    val:'R 2.1M', delta:'+31.2%',up:true},
                      ].map(r => (
                        <div key={r.n} style={{background:'#fff',borderRadius:6,padding:'5px 8px',display:'flex',alignItems:'center',gap:6,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                          <div style={{fontSize:7,color:'#aaa',width:14,fontFamily:"'JetBrains Mono',monospace"}}>{r.n}</div>
                          <div style={{flex:1,fontSize:8,fontWeight:600,color:'#0a0a10',fontFamily:"'Instrument Sans',sans-serif"}}>{r.name}</div>
                          <div style={{fontSize:8,fontWeight:700,color:'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{r.val}</div>
                          <div style={{fontSize:7,fontFamily:"'JetBrains Mono',monospace",color:r.up?'#17a85c':'#c44040',width:34,textAlign:'right'}}>{r.delta}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'0 8px 4px',fontSize:7,letterSpacing:'.12em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>TOP PRODUCTS</div>
                    <div style={{padding:'0 8px 8px',display:'flex',flexDirection:'column',gap:4}}>
                      {[
                        {n:'01',name:'Queen Bed Set Complete', val:'R 1.4M',units:'284 units'},
                        {n:'02',name:'3-Piece Lounge Suite',    val:'R 1.1M',units:'196 units'},
                      ].map(r => (
                        <div key={r.n} style={{background:'#fff',borderRadius:6,padding:'5px 8px',display:'flex',alignItems:'center',gap:6,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                          <div style={{fontSize:7,color:'#aaa',width:14,fontFamily:"'JetBrains Mono',monospace"}}>{r.n}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:8,fontWeight:600,color:'#0a0a10',fontFamily:"'Instrument Sans',sans-serif"}}>{r.name}</div>
                            <div style={{fontSize:7,color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>{r.units}</div>
                          </div>
                          <div style={{fontSize:8,fontWeight:700,color:'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{r.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Slide 2: Stock & Inventory */}
                  <div className={`lp-demo-slide${activeDemo===2?' active':''}`}>
                    <div className="lp-demo-section-tag">
                      <span>STOCK & INVENTORY</span>
                      <span className="lp-demo-live">● LIVE</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:5,padding:8}}>
                      {[
                        {l:'STOCK VALUE',v:'R 18.4M'},
                        {l:'DAYS ON HAND',v:'38d',warn:false},
                        {l:'ZERO STOCK',v:'54',warn:true},
                      ].map(k => (
                        <div key={k.l} style={{background:'#fff',borderRadius:6,padding:'7px 8px',boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}>
                          <div style={{fontSize:6,letterSpacing:'.08em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace",marginBottom:3}}>{k.l}</div>
                          <div style={{fontSize:11,fontWeight:800,color:k.warn?'#c44040':'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{k.v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'0 8px 4px',fontSize:7,letterSpacing:'.12em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>STOCK BY DEPARTMENT</div>
                    <div style={{padding:'0 8px 8px',display:'flex',flexDirection:'column',gap:4}}>
                      {[
                        {n:'01',name:'Home & Living',      val:'R 6.8M',sub:'24 lines · 1 840 units',w:100},
                        {n:'02',name:'Electronics',         val:'R 4.1M',sub:'18 lines · 2 104 units',w:60},
                        {n:'03',name:'Furniture',           val:'R 3.2M',sub:'31 lines · 890 units',  w:47},
                        {n:'04',name:'Outdoor & Garden',    val:'R 2.7M',sub:'22 lines · 614 units',  w:40},
                        {n:'05',name:'Kitchen & Dining',    val:'R 1.9M',sub:'56 lines · 4 210 units',w:28},
                      ].map(r => (
                        <div key={r.n} style={{background:'#fff',borderRadius:6,padding:'5px 8px',display:'flex',alignItems:'center',gap:6,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                          <div style={{fontSize:7,color:'#aaa',width:14,fontFamily:"'JetBrains Mono',monospace"}}>{r.n}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:8,fontWeight:600,color:'#0a0a10',fontFamily:"'Instrument Sans',sans-serif"}}>{r.name}</div>
                            <div style={{fontSize:6,color:'#aaa',fontFamily:"'JetBrains Mono',monospace"}}>{r.sub}</div>
                            <div style={{height:2,background:'#c4922a',borderRadius:1,marginTop:3,width:`${r.w}%`}} />
                          </div>
                          <div style={{fontSize:8,fontWeight:700,color:'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{r.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Slide 3: Debtors & Credit */}
                  <div className={`lp-demo-slide${activeDemo===3?' active':''}`}>
                    <div className="lp-demo-section-tag">
                      <span>DEBTORS & CREDIT</span>
                      <span className="lp-demo-live">● LIVE</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,padding:8}}>
                      {[
                        {l:'TOTAL BOOK',v:'R 38.6M',s:'94 active accounts',sc:'#888'},
                        {l:'OVERDUE 60+ DAYS',v:'R 14.2M',s:'37% of book',sc:'#c44040'},
                      ].map(k => (
                        <div key={k.l} style={{background: k.l==='OVERDUE 60+ DAYS'?'rgba(196,64,64,0.06)':'#fff',border:k.l==='OVERDUE 60+ DAYS'?'1px solid rgba(196,64,64,0.15)':'none',borderRadius:8,padding:'8px 10px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                          <div style={{fontSize:7,letterSpacing:'.1em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace",marginBottom:3}}>{k.l}</div>
                          <div style={{fontSize:12,fontWeight:800,color:k.l==='OVERDUE 60+ DAYS'?'#c44040':'#0a0a10',fontFamily:"'Syne',sans-serif"}}>{k.v}</div>
                          <div style={{fontSize:7,color:k.sc,marginTop:2,fontFamily:"'JetBrains Mono',monospace"}}>{k.s}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'0 8px 4px',fontSize:7,letterSpacing:'.12em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>AGEING BREAKDOWN</div>
                    <div style={{padding:'0 8px',display:'flex',flexDirection:'column',gap:4}}>
                      {[
                        {lbl:'Current',  val:'R 5.4M',  w:80,  col:'#17a85c'},
                        {lbl:'30 Days',  val:'R 4.9M',  w:75,  col:'#17a85c'},
                        {lbl:'60 Days',  val:'R 3.6M',  w:55,  col:'#c47c1a'},
                        {lbl:'90 Days',  val:'R 6.8M',  w:100, col:'#c44040'},
                        {lbl:'120 Days', val:'R 6.1M',  w:95,  col:'#c44040'},
                        {lbl:'180+ Days',val:'R 5.5M',  w:90,  col:'#8b0000'},
                      ].map(r => (
                        <div key={r.lbl} style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{fontSize:7,color:'#888',width:44,fontFamily:"'JetBrains Mono',monospace",flexShrink:0}}>{r.lbl}</div>
                          <div style={{flex:1,height:5,background:'rgba(0,0,0,0.06)',borderRadius:3}}>
                            <div style={{height:5,borderRadius:3,background:r.col,width:`${r.w}%`,opacity:.75}} />
                          </div>
                          <div style={{fontSize:8,fontWeight:700,color:'#0a0a10',fontFamily:"'Syne',sans-serif",width:40,textAlign:'right'}}>{r.val}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'8px 8px 4px',fontSize:7,letterSpacing:'.12em',textTransform:'uppercase',color:'#888',fontFamily:"'JetBrains Mono',monospace"}}>TOP OVERDUE ACCOUNTS</div>
                    <div style={{padding:'0 8px 8px',display:'flex',flexDirection:'column',gap:3}}>
                      {[
                        {name:'Eastside Trading',   overdue:'R 2.9M',days:'118 days'},
                        {name:'Northgate Supplies',  overdue:'R 2.1M',days:'87 days'},
                        {name:'Metro Furnishers',    overdue:'R 1.4M',days:'74 days'},
                      ].map(r => (
                        <div key={r.name} style={{background:'rgba(196,64,64,0.04)',border:'1px solid rgba(196,64,64,0.12)',borderRadius:5,padding:'5px 8px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                          <div style={{fontSize:8,fontWeight:600,color:'#0a0a10',fontFamily:"'Instrument Sans',sans-serif"}}>{r.name}</div>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:8,fontWeight:700,color:'#c44040',fontFamily:"'Syne',sans-serif"}}>{r.overdue}</div>
                            <div style={{fontSize:6,color:'#c44040',fontFamily:"'JetBrains Mono',monospace"}}>{r.days}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="lp-modules" id="modules">
        <div className="lp-modules-inner">
          <div className="lp-modules-header lp-reveal">
            <div className="lp-section-eyebrow">The Platform</div>
            <h2 className="lp-section-title">Six modules.<br /><em>One control system.</em></h2>
            <p className="lp-section-sub" style={{maxWidth:480}}>Every module is built specifically for retail — not adapted from a generic BI tool. Drill from executive summary down to individual product, branch or debtor account.</p>
          </div>
          <div className="lp-modules-grid lp-reveal">
            {[
              { icon:'📊', name:'Executive Overview', text:"Start every morning knowing exactly where your business stands. Revenue, margin, stock exposure and debtor risk — consolidated across every branch, in a single view.", features:['TODAY / MTD / QTD / YTD periods','Prior year comparison','Branch ranking','Live data refresh'] },
              { icon:'💰', name:'Sales & Revenue', text:"Know which branches are growing and which are slipping — before month-end. Drill from total revenue to department to individual product in four clicks. Every figure is live.", features:['Revenue & margin by branch','Department breakdown','Top products & units','Mobile-optimised drill-down'] },
              { icon:'📦', name:'Stock & Inventory', text:"Dead stock costs you money every day it sits. InsightPro shows you exactly what is overstocked, what is out of stock, and what has not moved in weeks — per branch, per product.", features:['Per-branch stock levels','Days on hand calculation','Slow mover detection','Item search across stores'] },
              { icon:'👥', name:'Debtors & Credit', text:"See exactly who owes you, how much, and for how long. Full ageing from Current to 180+ days. Flag high-risk accounts before they become write-offs.", features:['Full ageing buckets','Top overdue accounts','On-hold account tracking','Credit limit monitoring'] },
              { icon:'🏬', name:'Branch Performance', text:"Stop relying on branch managers to tell you how they are doing. Compare every store side by side — revenue, margin, units, growth. Find the problem branches before the month closes.", features:['Full branch comparison','Monthly revenue trend','Per-branch item search','YTD vs prior year'] },
              { icon:'🤖', name:'AI Advisor', text:"Ask any question about your business in plain language. Which branch needs attention? Who are my worst debtors? What is sitting dead in stock? The Advisor answers with your live data.", features:['Natural language queries','Full data context','Branch & debtor analysis','Instant responses'] },
            ].map(m => (
              <div className="lp-module" key={m.name}>
                <div className="lp-module-icon">{m.icon}</div>
                <div className="lp-module-name">{m.name}</div>
                <div className="lp-module-text">{m.text}</div>
                <div className="lp-module-features">
                  {m.features.map(f => <div className="lp-mf" key={f}>{f}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT SCREENS */}
      <section className="lp-screens" id="platform">
        <div className="lp-screens-inner">
          <div className="lp-screens-header lp-reveal">
            <div className="lp-section-eyebrow">See It In Action</div>
            <h2 className="lp-section-title">Built for <em>decision-makers,</em><br />not data analysts.</h2>
            <p className="lp-section-sub">Every screen works on mobile. No training required. If you can read a report, you can use InsightPro.</p>
          </div>
          <div className="lp-screens-grid lp-reveal">

            {/* Screen 1: Executive Overview */}
            <div className="lp-screen-card">
              <div className="lp-screen-label">
                <div className="lp-screen-name">Executive Overview</div>
                <div className="lp-screen-tag">LIVE</div>
              </div>
              <div className="lp-screen-body">
                <div className="lp-sm-kpis">
                  <div className="lp-sm-kpi"><div className="lp-sm-lbl">Total Revenue</div><div className="lp-sm-val">R 21.6M</div><div className="lp-sm-sub">↑ +9.4% vs prior</div></div>
                  <div className="lp-sm-kpi"><div className="lp-sm-lbl">Gross Margin</div><div className="lp-sm-val">74.2%</div><div className="lp-sm-sub" style={{color:'#888'}}>excl. overhead</div></div>
                  <div className="lp-sm-kpi"><div className="lp-sm-lbl">Units Sold</div><div className="lp-sm-val">12 480</div><div className="lp-sm-sub">current period</div></div>
                  <div className="lp-sm-kpi"><div className="lp-sm-lbl">Debtors 60+d</div><div className="lp-sm-val">R 14.2M</div><div className="lp-sm-sub dn">↑ 37% of book</div></div>
                </div>
                <div className="lp-sm-rows">
                  {[
                    { name:'City Central', sub:'5 240 units · Margin: 74.8%', val:'R 2.4M', delta:'+14.2%', up:true, w:90 },
                    { name:'Gateway',      sub:'4 680 units · Margin: 73.4%', val:'R 2.2M', delta:'+11.2%', up:true, w:78 },
                    { name:'Online Store', sub:'3 920 units · Margin: 91.2%', val:'R 1.8M', delta:'+19.3%', up:true, w:60 },
                    { name:'Southgate',    sub:'2 840 units · Margin: 71.6%', val:'R 1.5M', delta:'-4.1%',  up:false,w:55 },
                  ].map(r => (
                    <div className="lp-sm-row" key={r.name}>
                      <div className="lp-sm-row-left">
                        <div className="lp-sm-row-name">{r.name}</div>
                        <div className="lp-sm-row-sub">{r.sub}</div>
                        <div className="lp-sm-bar-wrap"><div className="lp-sm-bar" style={{width:`${r.w}%`}} /></div>
                      </div>
                      <div className="lp-sm-row-right">
                        <div className="lp-sm-row-val">{r.val}</div>
                        <div className="lp-sm-row-delta" style={{color: r.up ? '#17a85c':'#c44040'}}>{r.delta}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Screen 2: Debtors */}
            <div className="lp-screen-card">
              <div className="lp-screen-label">
                <div className="lp-screen-name">Debtors & Credit</div>
                <div className="lp-screen-tag">LIVE</div>
              </div>
              <div className="lp-screen-body">
                <div className="lp-sm-kpis" style={{marginBottom:12}}>
                  <div className="lp-sm-kpi"><div className="lp-sm-lbl">Total Book</div><div className="lp-sm-val">R 38.6M</div><div className="lp-sm-sub" style={{color:'#888'}}>94 accounts</div></div>
                  <div className="lp-sm-kpi"><div className="lp-sm-lbl">Overdue 60+d</div><div className="lp-sm-val" style={{color:'#c44040'}}>R 14.2M</div><div className="lp-sm-sub dn">37% of book</div></div>
                </div>
                <div style={{marginBottom:8}}>
                  <div className="lp-sm-lbl" style={{marginBottom:8}}>Ageing Breakdown</div>
                  <div className="lp-sm-ageing">
                    {[
                      { lbl:'Current',  val:'R 5.4M',  w:80, col:'#17a85c' },
                      { lbl:'30 Days',  val:'R 4.9M',  w:75, col:'#17a85c' },
                      { lbl:'60 Days',  val:'R 3.6M',  w:55, col:'#c47c1a' },
                      { lbl:'90 Days',  val:'R 6.8M',  w:100,col:'#c44040' },
                      { lbl:'120 Days', val:'R 6.1M',  w:95, col:'#c44040' },
                      { lbl:'180+ Days',val:'R 5.5M',  w:90, col:'#8b0000' },
                    ].map(r => (
                      <div className="lp-sm-age-row" key={r.lbl}>
                        <div className="lp-sm-age-lbl">{r.lbl}</div>
                        <div className="lp-sm-age-bar-wrap">
                          <div className="lp-sm-age-bar" style={{width:`${r.w}%`,background:r.col,opacity:.7}} />
                        </div>
                        <div className="lp-sm-age-val">{r.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Screen 3: Stock */}
            <div className="lp-screen-card">
              <div className="lp-screen-label">
                <div className="lp-screen-name">Stock & Inventory</div>
                <div className="lp-screen-tag">LIVE</div>
              </div>
              <div className="lp-screen-body">
                <div className="lp-sm-stock-kpis">
                  <div className="lp-sm-sk"><div className="lp-sm-sk-lbl">Stock Value</div><div className="lp-sm-sk-val">R 18.4M</div></div>
                  <div className="lp-sm-sk"><div className="lp-sm-sk-lbl">Days on Hand</div><div className="lp-sm-sk-val">38d</div></div>
                  <div className="lp-sm-sk"><div className="lp-sm-sk-lbl">Zero Stock</div><div className="lp-sm-sk-val warn">54</div></div>
                </div>
                <div className="lp-sm-lbl" style={{marginBottom:8}}>Stock Value by Department</div>
                <div className="lp-sm-rows">
                  {[
                    { name:'Home & Living',        sub:'24 lines · 1 840 units', val:'R 6.8M', w:100 },
                    { name:'Electronics',           sub:'18 lines · 2 104 units', val:'R 4.1M', w:60 },
                    { name:'Furniture',             sub:'31 lines · 890 units',   val:'R 3.2M', w:47 },
                    { name:'Outdoor & Garden',      sub:'22 lines · 614 units',   val:'R 2.7M', w:40 },
                    { name:'Kitchen & Dining',      sub:'56 lines · 4 210 units', val:'R 1.9M', w:28 },
                  ].map(r => (
                    <div className="lp-sm-row" key={r.name}>
                      <div className="lp-sm-row-left">
                        <div className="lp-sm-row-name">{r.name}</div>
                        <div className="lp-sm-row-sub">{r.sub}</div>
                        <div className="lp-sm-bar-wrap"><div className="lp-sm-bar" style={{width:`${r.w}%`}} /></div>
                      </div>
                      <div className="lp-sm-row-right">
                        <div className="lp-sm-row-val">{r.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Screen 4: AI Advisor */}
            <div className="lp-screen-card">
              <div className="lp-screen-label">
                <div className="lp-screen-name">AI Advisor</div>
                <div className="lp-screen-tag" style={{color:'#c4922a',background:'rgba(196,146,42,0.08)',borderColor:'rgba(196,146,42,0.2)'}}>POWERED BY CLAUDE</div>
              </div>
              <div className="lp-screen-body">
                <div className="lp-sm-chat">
                  <div className="lp-sm-msg user">Which branch needs the most attention this month?</div>
                  <div className="lp-sm-msg ai">
                    <strong>Southgate</strong> is your highest-risk branch this month. Revenue is down <strong>4.1%</strong> vs prior year — the only branch showing a decline. Their margin has also compressed to <strong>71.6%</strong>, 2.6 points below your network average.<br /><br />
                    Contributing factors: stock days at <strong>52d</strong> (above your 38d average) and two product categories — Furniture and Electronics — both down more than 8% MTD.
                  </div>
                  <div className="lp-sm-msg user">What about debtors?</div>
                  <div className="lp-sm-msg ai">
                    Your debtors position is <strong>critical</strong>. <strong>R 14.2M</strong> — 37% of your total book — is overdue 60+ days. Three accounts account for 68% of that exposure. The oldest has had no payment in <strong>118 days</strong>.
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="lp-integrations" id="integrations">
        <div className="lp-int-inner">
          <div className="lp-reveal">
            <div className="lp-section-eyebrow" style={{justifyContent:'flex-start'}}>Integrations</div>
            <h2 className="lp-section-title">Works with what<br /><em>you already have.</em></h2>
            <p className="lp-section-sub">No rip-and-replace. No migration project. No new software for your team to learn. InsightPro connects to your existing systems and runs alongside them.</p>
            <div className="lp-step-tech" style={{justifyContent:'flex-start',marginTop:24}}>
              {['On-Premises Gateway','Scheduled Refresh','POPIA Compliant','SSL Encrypted','South African Hosted'].map(t => (
                <div className="lp-tech-pill" key={t}>{t}</div>
              ))}
            </div>
          </div>
          <div className="lp-int-grid lp-reveal">
            {[
              { icon:'🏪', name:'IQ Retail',      type:'POS / ERP System' },
              { icon:'🖥️', name:'Vision Software', type:'POS / Retail System' },
              { icon:'🖥️', name:'VisionSoftware', type:'POS System' },
              { icon:'📊', name:'Power BI',       type:'Data Engine' },
              { icon:'📁', name:'Excel / CSV',    type:'Manual Data' },
              { icon:'☁️', name:'Google Cloud',   type:'Infrastructure' },
              { icon:'🔥', name:'Firebase',       type:'Auth & Database' },
              { icon:'🤖', name:'Claude AI',      type:'AI Advisor' },
              { icon:'📧', name:'Email Alerts',   type:'Notifications' },
            ].map(i => (
              <div className="lp-int-item" key={i.name}>
                <div className="lp-int-icon">{i.icon}</div>
                <div className="lp-int-name">{i.name}</div>
                <div className="lp-int-type">{i.type}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="lp-usecases">
        <div className="lp-usecases-inner">
          <div className="lp-usecases-header lp-reveal">
            <div className="lp-section-eyebrow">Who It's Built For</div>
            <h2 className="lp-section-title">Purpose-built for<br /><em>South African retail.</em></h2>
            <p className="lp-section-sub">InsightPro is not a generic BI tool adapted for retail. It was designed from the ground up for the specific challenges of multi-branch South African retail operations.</p>
          </div>
          <div className="lp-usecases-grid lp-reveal">
            {[
              { icon:'🛋️', industry:'Furniture & Appliances', title:'Multi-branch furniture retail', text:"Track revenue by branch, manage stock across locations, monitor instalment debtors and compare store performance — all from your phone. Built for the operational reality of furniture retail in South Africa." },
              { icon:'🛒', industry:'FMCG & Wholesale', title:'High-volume wholesale operations', text:"Handle high transaction volumes, monitor fast-moving stock, track customer credit exposure and identify which product lines are driving margin. Built for the pace of wholesale." },
              { icon:'🏭', industry:'Manufacturing', title:'Manufacturer-to-retail operations', text:"Track production output alongside sales performance. Monitor raw material stock, finished goods and customer debtors in a single system — without needing a data team." },
              { icon:'🏪', industry:'General Retail', title:'Any multi-location retail group', text:"If you run more than one store and you're still making decisions from spreadsheets and end-of-month reports, InsightPro gives you the visibility you need to run your business properly." },
            ].map(u => (
              <div className="lp-usecase" key={u.title}>
                <div className="lp-usecase-icon">{u.icon}</div>
                <div className="lp-usecase-content">
                  <div className="lp-usecase-industry">{u.industry}</div>
                  <div className="lp-usecase-title">{u.title}</div>
                  <div className="lp-usecase-text">{u.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="lp-pricing" id="pricing">
        <div className="lp-pricing-inner">
          <div className="lp-pricing-header lp-reveal">
            <div className="lp-section-eyebrow">Pricing</div>
            <h2 className="lp-section-title">Straightforward pricing.<br /><em>No surprises.</em></h2>
            <p className="lp-section-sub">All plans include setup, onboarding and support. No per-user fees. No hidden charges. Priced in ZAR.</p>
          </div>
          <div className="lp-pricing-grid lp-reveal">

            <div className="lp-plan">
              <div className="lp-plan-name">Starter</div>
              <div className="lp-plan-price">
                <span className="lp-plan-currency">R</span>
                <span className="lp-plan-amount">3 000</span>
                <span className="lp-plan-period"> / month</span>
              </div>
              <p className="lp-plan-desc">For single-location retailers who want to stop running their business from spreadsheets.</p>
              <div className="lp-plan-features">
                {['1 business location','Executive Overview dashboard','Sales & Revenue module','Stock & Inventory module','Up to 5 users','Email support'].map(f => <div className="lp-pf" key={f}>{f}</div>)}
                {['Debtors & Credit module','Branch Performance','AI Advisor'].map(f => <div className="lp-pf na" key={f}>{f}</div>)}
              </div>
              <button className="lp-plan-cta outline" onClick={() => scrollTo('contact')}>Get Started</button>
            </div>

            <div className="lp-plan featured">
              <div className="lp-plan-badge">Most Popular</div>
              <div className="lp-plan-name">Business</div>
              <div className="lp-plan-price">
                <span className="lp-plan-currency">R</span>
                <span className="lp-plan-amount">5 000</span>
                <span className="lp-plan-period"> / month</span>
              </div>
              <p className="lp-plan-desc">For multi-branch retailers who need to see every store, every metric, every morning.</p>
              <div className="lp-plan-features">
                {['Up to 10 branches','All 6 intelligence modules','Debtors & Credit full ageing','Branch Performance drill-down','AI Advisor','Up to 20 users','Priority support + onboarding'].map(f => <div className="lp-pf" key={f}>{f}</div>)}
              </div>
              <button className="lp-plan-cta primary" onClick={() => scrollTo('contact')}>Book a Demo</button>
            </div>

            <div className="lp-plan">
              <div className="lp-plan-name">Enterprise</div>
              <div className="lp-plan-price">
                <span className="lp-plan-currency">R</span>
                <span className="lp-plan-amount">8 000</span>
                <span className="lp-plan-period"> / month</span>
              </div>
              <p className="lp-plan-desc">For retail groups managing multiple companies, complex structures, or high transaction volumes.</p>
              <div className="lp-plan-features">
                {['Unlimited branches','Multi-company support','Custom DAX templates per entity','WhatsApp alerts & daily digest','Dedicated account manager','Custom integrations','SLA-backed support'].map(f => <div className="lp-pf" key={f}>{f}</div>)}
              </div>
              <button className="lp-plan-cta outline" onClick={() => scrollTo('contact')}>Contact Us</button>
            </div>

          </div>
          <p className="lp-pricing-note">All prices exclude VAT · Setup fee applies · Cancel anytime with 30 days notice</p>
        </div>
      </section>

      {/* TRUST */}
      <section className="lp-trust">
        <div className="lp-trust-inner lp-reveal">
          {[
            { icon:'🇿🇦', title:'Built for South Africa', text:"ZAR currency. POPIA compliant. SA-hosted infrastructure. Designed for local retail conditions — not adapted from a global product." },
            { icon:'🔒', title:'Your Data Never Leaves Site', text:"Raw financial data stays on your server. Only aggregated insights are published to your secure cloud workspace — fully POPIA compliant." },
            { icon:'📱', title:'Works on Any Device', text:"Every screen is built mobile-first. Check your dashboard from your phone at 6am before the day starts — or from your laptop in a board meeting." },
            { icon:'⚡', title:'No Spreadsheets. No Delays.', text:"No manual exports. No waiting for someone to send a report. Your data is live — query it from any device, any time." },
          ].map(t => (
            <div className="lp-trust-item" key={t.title}>
              <div className="lp-trust-icon">{t.icon}</div>
              <div className="lp-trust-title">{t.title}</div>
              <div className="lp-trust-text">{t.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section className="lp-contact" id="contact">
        <div className="lp-contact-inner">
          <div className="lp-reveal">
            <div className="lp-section-eyebrow" style={{justifyContent:'flex-start'}}>Book a Demo</div>
            <h2 className="lp-section-title">See it live<br /><em>with your own data.</em></h2>
            <p className="lp-section-sub">We connect to your system, run a sample load, and show you a live dashboard of your own business in a 45-minute session.</p>
            <div className="lp-contact-bullets">
              {['No commitment required — see it before you decide','We handle the technical setup, you bring the questions','Works with IQ Retail, Vision and most ERP systems','Your dashboard can be live within 5 business days'].map(b => (
                <div className="lp-contact-bullet" key={b}>{b}</div>
              ))}
            </div>
          </div>
          <div className="lp-reveal">
            {formState === FORM_SUCCESS ? (
              <div className="lp-form-success">
                <div className="lp-form-success-title">Request received ✓</div>
                <div className="lp-form-success-sub">We'll be in touch within one business day to arrange your demo.</div>
              </div>
            ) : (
              <div className="lp-form">
                <div className="lp-form-row">
                  <div className="lp-form-group"><label className="lp-form-label">First Name *</label><input className="lp-form-input" value={form.firstName} onChange={e => updateForm('firstName', e.target.value)} placeholder="Imraan" /></div>
                  <div className="lp-form-group"><label className="lp-form-label">Last Name</label><input className="lp-form-input" value={form.lastName} onChange={e => updateForm('lastName', e.target.value)} placeholder="Ganie" /></div>
                </div>
                <div className="lp-form-group"><label className="lp-form-label">Email Address *</label><input className="lp-form-input" type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} placeholder="you@company.co.za" /></div>
                <div className="lp-form-group"><label className="lp-form-label">Company Name *</label><input className="lp-form-input" value={form.company} onChange={e => updateForm('company', e.target.value)} placeholder="Your Retail Group" /></div>
                <div className="lp-form-row">
                  <div className="lp-form-group"><label className="lp-form-label">Number of Branches</label><input className="lp-form-input" value={form.branches} onChange={e => updateForm('branches', e.target.value)} placeholder="e.g. 8" /></div>
                  <div className="lp-form-group"><label className="lp-form-label">Current System</label><input className="lp-form-input" value={form.system} onChange={e => updateForm('system', e.target.value)} placeholder="IQ Retail, Vision, etc." /></div>
                </div>
                <div className="lp-form-group"><label className="lp-form-label">Message</label><textarea className="lp-form-input lp-form-textarea" value={form.message} onChange={e => updateForm('message', e.target.value)} placeholder="Tell us about your business and what you want to solve." /></div>
                {formError && <div className="lp-form-error">{formError}</div>}
                <button className="lp-form-submit" onClick={handleSubmit} disabled={formState === FORM_SENDING}>
                  {formState === FORM_SENDING ? 'Sending…' : 'Book My Demo →'}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <div className="lp-ctaband">
        <h2 className="lp-ctaband-title">Stop running your business<br /><em>from spreadsheets.</em></h2>
        <p className="lp-ctaband-sub">InsightPro is live and running for retail operations right now. Your dashboard can be up within a week — no disruption, no migration, no IT project.</p>
        <div className="lp-ctaband-actions">
          <button className="lp-btn-primary" onClick={() => scrollTo('contact')}>Book a Live Demo →</button>
          <button className="lp-btn-outline" onClick={() => navigate('/login')}>Sign In</button>
        </div>
        <div className="lp-ctaband-badges">
          {['No lock-in contracts','Live in 5 days','SA-hosted & POPIA compliant','Full training included'].map(b => (
            <div className="lp-badge" key={b}>{b}</div>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-top">
            <div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div className="lp-logo-hex"><span>IP</span></div>
                <div className="lp-brand">InsightPro<sup>™</sup></div>
              </div>
              <p className="lp-footer-brand-text">Retail intelligence platform for multi-branch operations. Real-time visibility. Total control.</p>
            </div>
            <div>
              <div className="lp-footer-col-title">Platform</div>
              <ul className="lp-footer-links">
                <li><a onClick={() => scrollTo('modules')}>Executive Overview</a></li>
                <li><a onClick={() => scrollTo('modules')}>Sales & Revenue</a></li>
                <li><a onClick={() => scrollTo('modules')}>Stock & Inventory</a></li>
                <li><a onClick={() => scrollTo('modules')}>Debtors & Credit</a></li>
                <li><a onClick={() => scrollTo('modules')}>AI Advisor</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-col-title">Company</div>
              <ul className="lp-footer-links">
                <li><a onClick={() => scrollTo('how-it-works')}>How It Works</a></li>
                <li><a onClick={() => scrollTo('integrations')}>Integrations</a></li>
                <li><a onClick={() => scrollTo('pricing')}>Pricing</a></li>
                <li><a onClick={() => scrollTo('contact')}>Book a Demo</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-col-title">Contact</div>
              <ul className="lp-footer-links">
                <li><a href="mailto:info@insightpro.co.za">info@insightpro.co.za</a></li>
                <li><a href="https://www.insightpro.co.za">www.insightpro.co.za</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <div className="lp-footer-copy">© {new Date().getFullYear()} InsightPro™ · All rights reserved</div>
            <div className="lp-footer-legal">
              <a onClick={() => navigate('/legal?tab=privacy')}>Privacy Policy</a>
              <a onClick={() => navigate('/legal?tab=popia')}>POPIA</a>
              <a onClick={() => navigate('/legal?tab=terms')}>Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
